import { useProfileData } from '../AuthProvider'
import { getChannelListOptions } from '../Chat/channelListOptions'
import { useThemeContext } from '../Chat/context/Theme'
import {
  useConnectUser,
  useChecklist,
  useUpdateAppHeightOnResize,
} from '../Chat/hooks'
import * as chatEmbedLayout from '../Chat/chatEmbedLayout.module.scss'
import * as messagesViewport from '../Chat/messagesViewport.module.scss'
import '../Chat/styles/index.css'
import MessagingChannelPreview from '../Chat/components/MessagingChannelPreview/MessagingChannelPreview'
import Template from '../Template'
import SidebarNavigation from '../SidebarNavigation'
import * as dashboardLayoutStyles from '../PageDashboard/style.module.scss'
import classNames from 'classnames'
import React, { useCallback, useMemo } from 'react'
import { Chat as BaseChat, ChannelList } from 'stream-chat-react'
import 'stream-chat-react/dist/css/v2/index.css'

const CRM_PATH_PREFIXES = ['/dashboard', '/message', '/messages', '/account']

function isCrmRoute(pathname) {
  const p = pathname || ''
  return CRM_PATH_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`),
  )
}

function getAccountSettingsNavState(pathname) {
  const p = pathname || ''
  const onAccount = p === '/account' || p.startsWith('/account/')
  if (!onAccount) {
    return { accountSettingsNav: false, accountSettingsActiveItem: 'workspace' }
  }
  if (p === '/account/team' || p.startsWith('/account/team/')) {
    return { accountSettingsNav: true, accountSettingsActiveItem: 'team' }
  }
  if (p === '/account/payments' || p.startsWith('/account/payments/')) {
    return { accountSettingsNav: true, accountSettingsActiveItem: 'payments' }
  }
  return { accountSettingsNav: true, accountSettingsActiveItem: 'workspace' }
}

/** `/message` thread UI — not `/messages` (inbox list). */
function isCrmMessageThreadPath(p) {
  return p === '/message' || p.startsWith('/message/')
}

function CrmLayoutNoStream({
  onDashboardNavClick,
  activeItem,
  accountSettingsNav,
  accountSettingsActiveItem,
  children,
}) {
  return (
    <Template fullWidth dashboardLayout>
      <div className={chatEmbedLayout.root}>
        <div
          className={classNames(
            dashboardLayoutStyles.pageWrapper,
            messagesViewport.pageRow,
          )}
        >
          <SidebarNavigation
            layout='dashboard'
            messageBadgeCount={24}
            activeItem={activeItem}
            accountSettingsNav={accountSettingsNav}
            accountSettingsActiveItem={accountSettingsActiveItem}
            onDashboardNavClick={onDashboardNavClick}
            channelListSlot={null}
          />
          <div className={dashboardLayoutStyles.mainArea}>{children}</div>
        </div>
      </div>
    </Template>
  )
}

/**
 * Single Stream connection + sidebar channel list; stays mounted across
 * /dashboard/, /messages/, and /message/ (see gatsby-browser `wrapPageElement`).
 */
function CrmAppShellConnected({
  profile,
  userToken,
  activeItem,
  onDashboardNavClick,
  accountSettingsNav,
  accountSettingsActiveItem,
  children,
  location,
}) {
  const { themeClassName } = useThemeContext()

  const userToConnect = useMemo(
    () => ({ id: profile.user_id }),
    [profile.user_id],
  )

  const channelListOptions = useMemo(
    () => getChannelListOptions(true, profile.user_id),
    [profile.user_id],
  )

  const targetOrigin =
    typeof window !== 'undefined' ? window.location.origin : ''

  const chatClient = useConnectUser(
    process.env.GATSBY_STREAM_API_KEY || '',
    userToConnect,
    userToken,
  )

  useChecklist(chatClient, targetOrigin)
  useUpdateAppHeightOnResize()

  // Keep `BaseChat` + `ChannelList` mounted once connected; account settings hides
  // the list in the sidebar (see SidebarNavigation) instead of unmounting Stream.
  if (!chatClient) {
    return (
      <CrmLayoutNoStream
        activeItem={activeItem}
        onDashboardNavClick={onDashboardNavClick}
        accountSettingsNav={accountSettingsNav}
        accountSettingsActiveItem={accountSettingsActiveItem}
      >
        {children}
      </CrmLayoutNoStream>
    )
  }

  const channelListSlot = (
    <ChannelList
      {...channelListOptions}
      setActiveChannelOnMount={
        !isCrmMessageThreadPath(location?.pathname ?? '')
      }
      renderChannels={(loadedChannels, renderChannel) =>
        loadedChannels.map((ch) =>
          React.cloneElement(renderChannel(ch), {
            key: ch.cid || ch.id || 'channel',
          }),
        )
      }
      Preview={(previewProps) => (
        <MessagingChannelPreview
          {...previewProps}
          listParentPathname={location?.pathname ?? ''}
        />
      )}
    />
  )

  return (
    <Template fullWidth dashboardLayout>
      <BaseChat client={chatClient} theme={`messaging ${themeClassName}`}>
        <div className={chatEmbedLayout.root}>
          <div
            className={classNames(
              dashboardLayoutStyles.pageWrapper,
              messagesViewport.pageRow,
            )}
          >
            <SidebarNavigation
              layout='dashboard'
              messageBadgeCount={24}
              activeItem={activeItem}
              accountSettingsNav={accountSettingsNav}
              accountSettingsActiveItem={accountSettingsActiveItem}
              onDashboardNavClick={onDashboardNavClick}
              channelListSlot={channelListSlot}
            />
            <div className={dashboardLayoutStyles.mainArea}>{children}</div>
          </div>
        </div>
      </BaseChat>
    </Template>
  )
}

export default function CrmAppShell({ location, children }) {
  // Only CRM routes (dashboard/messages/account) require a login. Public routes
  // such as the customer reservation tracker (`/reservation/<CODE>`) must render
  // for unauthenticated visitors — the code itself is the capability token (see
  // ADR-0001). `useProfileData` redirects to `/` when no token is present
  // unless told the route is optional, so gate the redirect on the route.
  const crmRoute = isCrmRoute(location?.pathname)
  const profileResult = useProfileData({
    optional: !crmRoute,
    withSession: crmRoute,
  })
  const profile = crmRoute ? profileResult?.profile : profileResult
  const userToken = crmRoute ? profileResult?.userToken : null
  const isLoading = crmRoute ? profileResult?.isLoading : false

  const activeItem = useMemo(() => {
    const p = location?.pathname || ''
    if (p === '/messages' || p.startsWith('/messages/')) return 'messages'
    if (isCrmMessageThreadPath(p)) return 'messages'
    return 'dashboard'
  }, [location?.pathname])

  const { accountSettingsNav, accountSettingsActiveItem } = useMemo(
    () => getAccountSettingsNavState(location?.pathname ?? ''),
    [location?.pathname],
  )

  const onDashboardNavClick = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('crm-dashboard-nav-reset'))
    }
  }, [])

  if (!crmRoute) {
    return children
  }

  if (isLoading || !profile?.user_id || !userToken) {
    return (
      <CrmLayoutNoStream
        activeItem={activeItem}
        onDashboardNavClick={onDashboardNavClick}
        accountSettingsNav={accountSettingsNav}
        accountSettingsActiveItem={accountSettingsActiveItem}
      >
        {children}
      </CrmLayoutNoStream>
    )
  }

  return (
    <CrmAppShellConnected
      profile={profile}
      userToken={userToken}
      activeItem={activeItem}
      onDashboardNavClick={onDashboardNavClick}
      accountSettingsNav={accountSettingsNav}
      accountSettingsActiveItem={accountSettingsActiveItem}
      location={location}
    >
      {children}
    </CrmAppShellConnected>
  )
}
